import React, { useCallback, useState, useEffect, useRef } from "react";
import { useDropzone } from "react-dropzone";
import { Alert, Box, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, Grid, IconButton, InputLabel, LinearProgress, Select, MenuItem, Tooltip, Typography, useTheme, TextField, Button, Checkbox, Chip } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import RefreshIcon from "@mui/icons-material/Refresh";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import { v4 as uuidv4 } from "uuid";
import { tokens } from "../../theme";
import { ARTIFICIAL_INTELLIGENCE, MARKET_INDENTED_LIST } from "../../utilities/constants";
import { DataGrid } from "@mui/x-data-grid";
import { useSelector } from "react-redux";
import { getLocalDateTime, getSortedMarket } from "../../utilities/helpers";
import ModalDialog from "../../components/ModalDialog";
import codeTablePostgresApi from "../../apis/codeTablePostgresApi";
import { EqipItem } from "../../components/EqipItem";
import ieqipCoreServiceApi from "../../apis/ieqipCoreServiceApi";

const formatFileSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const OPENAI_ASSISTANT_NAME = process.env.REACT_APP_AI_ASSISTANT;

const AIv2 = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [errorMessage, setErrorMessage] = useState(null);
  const [aiPipeconeDocuments, setAiPipeconeDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [markets, setMarkets] = useState([]);
  const [selectedMarkets, setSelectedMarkets] = useState([]);
  const [commodities, setCommodities] = useState([]);
  const [selectedcommodities, setSelectedCommodities] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(100);
  const [searchText, setSearchText] = useState(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const [openDeleteAllModal, setOpenDeleteAllModal] = useState(false);
  const [isDeletingAll, setIsDeletingAll] = useState(false);
  const [deleteAllProgress, setDeleteAllProgress] = useState({ total: 0, deleted: 0, done: false });
  const [deleteAllConfirmText, setDeleteAllConfirmText] = useState("");
  const [openDeleteSelectedModal, setOpenDeleteSelectedModal] = useState(false);
  const [isDeletingSelected, setIsDeletingSelected] = useState(false);
  // Background OpenAI vector store cleanup tracking — uploads are blocked while
  // a cleanup is in progress for the selected commodity/market.
  const [cleanupInProgress, setCleanupInProgress] = useState(false);
  const [cleanupProgress, setCleanupProgress] = useState({ deleted: 0, total: 0 });
  const [successMessage, setSuccessMessage] = useState(null);
  const cleanupPollRef = useRef(null);

  const idToken = useSelector((state) => {
    return state.token;
  });

  const allowedCommodities = useSelector((state) => {
    return state.allowedCommodities;
  });

  const allowedMarkets = useSelector((state) => {
    return state.allowedMarkets;
  });

  const requestHeader = {
    headers: {
      Authorization: idToken,
    },
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // Check the background vector store cleanup status for the selected
  // commodity/market. Returns 'running' | 'done' | 'unknown'. On an error we
  // return 'unknown' and do NOT change the blocking state, so a transient
  // failure can never prematurely unblock uploads.
  const checkCleanupStatus = useCallback(async () => {
    if (selectedcommodities?.length <= 0 || selectedMarkets?.length <= 0) {
      setCleanupInProgress(false);
      return 'done';
    }
    try {
      const { data } = await ieqipCoreServiceApi.get(
        `/api/aiassistantdocuments/cleanup-status?commodity=${selectedcommodities[0]}&market=${selectedMarkets[0]}`,
        requestHeader
      );
      const running = !!data?.in_progress;
      setCleanupInProgress(running);
      if (running) {
        setCleanupProgress({ deleted: data.deleted || 0, total: data.total || 0 });
      }
      return running ? 'running' : 'done';
    } catch (error) {
      // Unknown — keep the current blocking state, keep polling.
      return 'unknown';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedcommodities, selectedMarkets]);

  // Poll while a cleanup is in progress; only stop + unblock on a definitive
  // 'done'. An 'unknown' (transient error) keeps the block and keeps polling.
  const startCleanupPolling = useCallback(() => {
    if (cleanupPollRef.current) clearInterval(cleanupPollRef.current);
    cleanupPollRef.current = setInterval(async () => {
      const status = await checkCleanupStatus();
      if (status === 'done') {
        clearInterval(cleanupPollRef.current);
        cleanupPollRef.current = null;
        setCleanupProgress({ deleted: 0, total: 0 });
        setSuccessMessage("Cleanup complete. You can now upload new files.");
        fetchData();
      }
    }, 5000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkCleanupStatus]);

  // When the commodity/market selection changes, refresh cleanup status and
  // resume polling if a cleanup is already running (e.g. started elsewhere).
  useEffect(() => {
    (async () => {
      const status = await checkCleanupStatus();
      if (status === 'running') startCleanupPolling();
    })();
    return () => {
      if (cleanupPollRef.current) {
        clearInterval(cleanupPollRef.current);
        cleanupPollRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedcommodities, selectedMarkets]);

  async function fetchData() {
    setIsLoading(true);

    let url = `/api/aiassistantdocumentswithpaging?page=${page + 1}&page_size=${pageSize}`;
    if (searchText) {
      url += `&original_filename=${searchText}`;
    }
    if (selectedcommodities?.length > 0) {
      url += `&commodity=${selectedcommodities[0]}`;
    }
    if (selectedMarkets?.length > 0) {
      url += `&market=${selectedMarkets[0]}`;
    }

    const [responseCodeTable, responseDocumentApi] = await Promise.all([
      codeTablePostgresApi.get(`/codetables/codetabletypes/12,13`), 
      ieqipCoreServiceApi.get(url, requestHeader)
    ]);

    let documents = responseDocumentApi?.data?.documents || [];
    
    // If viewing warning letters, fetch and merge their extraction status
    if (selectedMarkets?.length > 0 && selectedMarkets[0] === 'WARNINGUNTITLEDLETTER') {
      try {
        const warningLetterResponse = await ieqipCoreServiceApi.get(
          `/api/warning-letter/extractions?page=${page + 1}&page_size=${pageSize}`,
          requestHeader
        );
        
        const warningLetterExtractions = warningLetterResponse?.data || [];
        
        // Create a map of document IDs to extraction status
        const statusMap = new Map();
        warningLetterExtractions?.warning_letters?.forEach(extraction => {
          statusMap.set(extraction.ai_assistant_document_id, extraction.extraction_status);
        });
        
        // Merge extraction status into documents
        documents = documents.map(doc => ({
          ...doc,
          extraction_status: statusMap.get(doc.id) || doc.extraction_status
        }));
      } catch (error) {
        console.error('Failed to fetch warning letter extraction status:', error);
      }
    }

    setAiPipeconeDocuments(documents);
    setTotalCount(responseDocumentApi.data.total_count);
    const codeTables = responseCodeTable.data.body;
    setCommodities(codeTables
      .filter((codeTable) => codeTable.code_table_type_id === 12 && allowedCommodities.includes(codeTable.code))
      .sort((a, b) => a.description.localeCompare(b.description)));
    const sortedMarkets = getSortedMarket(codeTables, allowedMarkets);
    setMarkets(sortedMarkets);
    setIsLoading(false);
  }

  const loggedInUser = useSelector((state) => {
    return state.user;
  });

  // Track per-file upload status: { id, name, size, status: 'uploading'|'success'|'error', error? }
  const [fileUploads, setFileUploads] = useState([]);
  const fileUploadsRef = useRef([]);

  const updateFileStatus = (id, updates) => {
    fileUploadsRef.current = fileUploadsRef.current.map((f) =>
      f.id === id ? { ...f, ...updates } : f
    );
    setFileUploads([...fileUploadsRef.current]);
  };

  const removeFileUpload = (id) => {
    fileUploadsRef.current = fileUploadsRef.current.filter((f) => f.id !== id);
    setFileUploads([...fileUploadsRef.current]);
  };

  const uploadSingleFile = async (acceptedFile, fileId, bucketName, vectorStoreName) => {
    // Single backend call: the server uploads to the vector store, S3 and DB
    // as one transaction and rolls everything back if any step fails.
    try {
      const formData = new FormData();
      formData.append("files", acceptedFile);
      formData.append("assistant_name", OPENAI_ASSISTANT_NAME);
      formData.append("vector_store_name", vectorStoreName);
      formData.append("commodity", selectedcommodities[0]);
      formData.append("market", selectedMarkets[0]);
      formData.append("bucket_name", bucketName);
      formData.append("created_by", loggedInUser.email);

      const jobResponse = await ieqipCoreServiceApi.post(
        "/api/openai/assistant/v2/uploadfiles",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      const jobId = jobResponse.data.job_id;
      const jobStatus = await new Promise((resolve, reject) => {
        const poll = setInterval(async () => {
          try {
            const { data } = await ieqipCoreServiceApi.get(
              `/api/openai/assistant/job/${jobId}`
            );
            if (data.status === "completed") {
              clearInterval(poll);
              resolve(data);
            } else if (data.status === "failed") {
              clearInterval(poll);
              reject(new Error(data.error || "Upload failed"));
            }
          } catch (err) {
            clearInterval(poll);
            reject(err);
          }
        }, 4000);
      });

      // The job processes each file atomically and reports a per-file result.
      const result = (jobStatus.results || []).find(
        (r) => r.original_filename === acceptedFile.name
      );
      if (result && result.status === "failed") {
        updateFileStatus(fileId, { status: "error", error: result.error || "Upload failed" });
        return;
      }

      updateFileStatus(fileId, { status: "success" });
      fetchData();
    } catch (error) {
      updateFileStatus(fileId, {
        status: "error",
        error: error.response?.data?.detail || error.message || "Upload failed",
      });
    }
  };

  const onDrop = useCallback(
    async (acceptedFiles) => {
      setErrorMessage(null);

      if (acceptedFiles?.length > ARTIFICIAL_INTELLIGENCE.MAX_FILE_UPLOAD_TO_PIPECONE) {
        setErrorMessage(`Please upload up to ${ARTIFICIAL_INTELLIGENCE.MAX_FILE_UPLOAD_TO_PIPECONE} files`);
        return;
      }
      if (selectedcommodities?.length <= 0) {
        setErrorMessage("Please select a Commodity");
        return;
      }
      if (selectedMarkets?.length <= 0) {
        setErrorMessage("Please select a Market");
        return;
      }
      if (cleanupInProgress) {
        setErrorMessage(
          "A cleanup is still running for this Commodity / Market. Please wait until it completes before uploading new files."
        );
        return;
      }

      const bucketName = process.env.REACT_APP_S3_IEQIP_AI_DOCUMENT;
      const vectorStoreName = `${selectedcommodities[0]}_${selectedMarkets[0]}_${process.env.REACT_APP_ENVIRONMENT}`;

      // Build initial upload entries and add them to state
      const newEntries = acceptedFiles.map((file) => ({
        id: uuidv4(),
        name: file.name,
        size: file.size,
        status: "uploading",
        error: null,
      }));

      fileUploadsRef.current = [...newEntries, ...fileUploadsRef.current];
      setFileUploads([...fileUploadsRef.current]);

      // Fire all uploads in parallel — small files finish first naturally
      acceptedFiles.forEach((file, idx) => {
        uploadSingleFile(file, newEntries[idx].id, bucketName, vectorStoreName);
      });
    },
    // eslint-disable-next-line
    [loggedInUser?.email, selectedcommodities, selectedMarkets, cleanupInProgress]
  );

  const handleCommodityChange = (event) => {
    const {
      target: { value },
    } = event;
    setSelectedCommodities(
      // On autofill we get a stringified value.
      typeof value === "string" ? value.split(",") : value
    );
  };

  const handleMarketChange = (event) => {
    const {
      target: { value },
    } = event;
    setSelectedMarkets(
      // On autofill we get a stringified value.
      typeof value === "string" ? value.split(",") : value
    );
  };

  const handleDeleteAllOk = async () => {
    setIsDeletingAll(true);
    setDeleteAllProgress({ total: 0, deleted: 0, current_file: '', done: false, elapsed: 0, eta: null });
    setErrorMessage(null);
    try {
      const commodity = selectedcommodities[0];
      const market = selectedMarkets[0];
      const environment = process.env.REACT_APP_ENVIRONMENT;
      const bucketName = process.env.REACT_APP_S3_IEQIP_AI_DOCUMENT;
      const baseUrl = process.env.REACT_APP_IEQIP_CORE_SERVICE_API_URL;
      const url = `${baseUrl}/api/aiassistantdocuments/bulk/stream?commodity=${commodity}&market=${market}&environment=${environment}&assistant_name=${OPENAI_ASSISTANT_NAME}&bucket_name=${bucketName}`;

      const response = await fetch(url, {
        method: 'DELETE',
        headers: { Authorization: idToken },
      });

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let lastErrors = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              setDeleteAllProgress(data);
              if (data.errors) lastErrors = data.errors;
            } catch (e) { /* ignore */ }
          }
        }
      }

      // Documents that failed to delete are kept intact — report them.
      if (lastErrors.length) {
        setErrorMessage(
          `Some documents could not be deleted and were left intact:\n${lastErrors.join("\n")}`
        );
      }

      // The DB + S3 are cleared, but the OpenAI vector store cleanup continues
      // in the background. Block uploads and poll until it reports completed.
      setCleanupInProgress(true);
      startCleanupPolling();

      await fetchData();
    } catch (error) {
      console.error("handleDeleteAllOk", error);
      setErrorMessage(`Unable to delete all documents. ${error.message}`);
      setOpenDeleteAllModal(false);
    } finally {
      setIsDeletingAll(false);
    }
  };

  const handleDeleteAllClose = () => {
    setOpenDeleteAllModal(false);
    setDeleteAllProgress({ total: 0, deleted: 0, current_file: '', done: false, elapsed: 0, eta: null });
    setDeleteAllConfirmText("");
  };

  const handleDeleteSelectedOk = async () => {
    if (selectedRows.length === 0) return;
    setIsDeletingSelected(true);
    setErrorMessage(null);
    try {
      const selectedDocuments = aiPipeconeDocuments.filter((doc) => selectedRows.includes(doc.id));
      const documentUids = selectedDocuments.map((doc) => doc.ai_assistant_document_uid);

      const { data } = await ieqipCoreServiceApi.post("/api/aiassistantdocuments/batch/delete", {
        document_uids: documentUids,
        environment: process.env.REACT_APP_ENVIRONMENT,
        assistant_name: OPENAI_ASSISTANT_NAME,
        bucket_name: process.env.REACT_APP_S3_IEQIP_AI_DOCUMENT,
      }, requestHeader);

      // Each document is deleted atomically; surface any per-document failures.
      if (data?.errors?.length) {
        setErrorMessage(
          `Some documents could not be deleted and were left intact:\n${data.errors.join("\n")}`
        );
      }

      setSelectedRows([]);
      await fetchData();
    } catch (error) {
      console.error("handleDeleteSelectedOk", error);
      setErrorMessage(`Failed to delete selected documents. ${error.response?.data?.detail || error.message}`);
    } finally {
      setIsDeletingSelected(false);
      setOpenDeleteSelectedModal(false);
    }
  };

  const handleDonwload = async (aiAssistantDocument) => {
    try {
      const bucketName = process.env.REACT_APP_S3_IEQIP_AI_DOCUMENT;
      const folder = `${aiAssistantDocument.commodity}_${aiAssistantDocument.market}_${process.env.REACT_APP_ENVIRONMENT}`;
      const response = await ieqipCoreServiceApi.get(`/api/s3/v2/download/${bucketName}/${folder}/${aiAssistantDocument.uploaded_filename}`, {
        responseType: "blob",
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", aiAssistantDocument.original_filename || aiAssistantDocument.uploaded_filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed", error);
    } finally {
    }
  };



  const handleSearchText = async () => {
    try {
      setIsLoading(true);
      let url = `/api/aiassistantdocumentswithpaging?page=${page + 1}&page_size=${pageSize}`;
      if (searchText) {
        url += `&original_filename=${searchText}`;
      }
      if (selectedcommodities?.length > 0) {
        url += `&commodity=${selectedcommodities[0]}`;
      }
      if (selectedMarkets?.length > 0) {
        url += `&market=${selectedMarkets[0]}`;
      }
      const responseDocumentApi = await ieqipCoreServiceApi.get(url);
      setAiPipeconeDocuments(responseDocumentApi.data.documents);
      setTotalCount(responseDocumentApi.data.total_count);
    } catch (error) {
      console.error("handleSearchText", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefreshStatus = async () => {
    try {
      setIsLoading(true);
      await fetchData();
      setErrorMessage(null);
    } catch (error) {
      console.error("handleRefreshStatus", error);
      setErrorMessage(`Failed to refresh status: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });
  
  const getExtractionStatusColor = (status) => {
    switch (status) {
      case "completed":
        return "success";
      case "pending":
        return "warning";
      case "failed":
        return "error";
      default:
        return "default";
    }
  };

  const columns = [
    {
      field: "original_filename",
      headerName: "File Name",
      flex: 4,
      minWidth: 300,
      renderCell: ({ row }) => {
        return (
          <>
            <Tooltip title={row.original_filename}>{row.original_filename}</Tooltip>
          </>
        );
      },
    },
    {
      field: "commodity",
      headerName: "Commodity",
      flex: 1,
      minWidth: 100,
    },
    {
      field: "market",
      headerName: "Market",
      flex: 1,
      minWidth: 100,
    },
    {
      field: "extraction_status",
      headerName: "Extraction Status",
      flex: 1,
      minWidth: 140,
      renderCell: ({ row }) => {
        if (!row.extraction_status) {
          return <Typography variant="body2" color="text.secondary">-</Typography>;
        }
        return (
          <Chip 
            label={row.extraction_status.toUpperCase()} 
            color={getExtractionStatusColor(row.extraction_status)}
            size="small"
          />
        );
      },
    },
    {
      field: "created_by",
      headerName: "Created By",
      flex: 1,
      minWidth: 120,
    },
    {
      field: "created_date",
      headerName: "Created Date",
      flex: 1,
      minWidth: 150,
      renderCell: ({ row }) => {
        return <>{getLocalDateTime(row.created_date)}</>;
      },
    },
    {
      field: "ai_pipecone_document_uid",
      headerName: "Actions",
      flex: 0.5,
      minWidth: 100,
      renderCell: ({ row }) => {
        return (
          <Box>
            <IconButton color="success" onClick={() => handleDonwload(row)}>
              <FileDownloadIcon />
            </IconButton>
          </Box>
        );
      },
    },
  ];
  return (
    <Box m="20px">
      {errorMessage && <Alert severity="error" sx={{ whiteSpace: 'pre-line' }}>{errorMessage}</Alert>}
      {successMessage && <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ mb: 1 }}>{successMessage}</Alert>}
      <Box>
        <FormControl sx={{ minWidth: 300, marginTop: 1, marginBottom: 1, marginRight: 1 }}>
          <InputLabel id="commodity-label">Commodity</InputLabel>
          <Select fullWidth labelId="commodity-label" label="Commodity" value={selectedcommodities} onChange={handleCommodityChange} renderValue={(selected) => selected.join(", ")}>
            {commodities.map((option) => (
              <MenuItem value={option.code}>
                <Checkbox checked={selectedcommodities.indexOf(option.code) > -1} />
                {`${option.description}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 300, marginTop: 1, marginBottom: 1 }}>
          <InputLabel id="market-label">Market</InputLabel>
          <Select fullWidth labelId="market-label" label="Market" value={selectedMarkets} onChange={handleMarketChange} renderValue={(selected) => selected.join(", ")}>
            {markets.map((option) => (
              <MenuItem value={option.code}>
                {MARKET_INDENTED_LIST.includes(option.code) && <div>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</div>}
                <Checkbox checked={selectedMarkets.indexOf(option.code) > -1} />
                {`${option.description}`}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Box>
        <FormControl fullWidth sx={{ marginTop: 1, marginBottom: 1 }}>
          <Grid container spacing={1}>
            <Grid item xs={11}>
              <EqipItem>
                <TextField fullWidth label="Enter file name" value={searchText} onChange={(e) => setSearchText(e.target.value)} />
              </EqipItem>
            </Grid>
            <Grid item xs={1}>
              <Button variant="contained" onClick={handleSearchText}>
                Search
              </Button>
            </Grid>
          </Grid>
        </FormControl>
      </Box>
      <Box sx={{ marginTop: 1, marginBottom: 1, display: 'flex', gap: 1 }}>
        <Button
          variant="outlined"
          color="secondary"
          startIcon={<RefreshIcon />}
          onClick={handleRefreshStatus}
          disabled={isLoading}
        >
          Refresh Status
        </Button>
        {selectedRows.length > 0 && (
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setOpenDeleteSelectedModal(true)}
            disabled={isDeletingSelected}
          >
            {isDeletingSelected ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            Delete Selected ({selectedRows.length})
          </Button>
        )}
        {selectedcommodities.length > 0 && selectedMarkets.length > 0 && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setOpenDeleteAllModal(true)}
            disabled={isLoading || isDeletingAll}
          >
            Delete All
          </Button>
        )}
      </Box>
      {cleanupInProgress && (
        <Alert
          severity="info"
          icon={<CircularProgress size={20} />}
          sx={{ mb: 1 }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Cleaning up the AI knowledge base for {selectedcommodities[0]} / {selectedMarkets[0]}…
          </Typography>
          <Typography variant="caption" sx={{ display: 'block' }}>
            {cleanupProgress.total > 0
              ? `Removed ${cleanupProgress.deleted} of ${cleanupProgress.total} files from OpenAI. Uploads are disabled until this finishes.`
              : 'Removing old files from the OpenAI vector store. Uploads are disabled until this finishes.'}
            {' '}This may take a few minutes for large stores.
          </Typography>
          <LinearProgress
            variant={cleanupProgress.total > 0 ? 'determinate' : 'indeterminate'}
            value={cleanupProgress.total > 0 ? (cleanupProgress.deleted / cleanupProgress.total) * 100 : 0}
            sx={{ mt: 1, borderRadius: 1 }}
          />
          {cleanupProgress.total > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', textAlign: 'right' }}>
              {cleanupProgress.deleted}/{cleanupProgress.total}
            </Typography>
          )}
        </Alert>
      )}
      <Box>
        {cleanupInProgress ? (
          <Box
            sx={{
              p: 2,
              border: "1px dashed grey",
              textAlign: "center",
              opacity: 0.5,
              cursor: "not-allowed",
              backgroundColor: "#f5f5f5",
            }}
          >
            <Typography color="text.secondary">
              Uploads are disabled while cleanup is in progress. Please wait until it completes.
            </Typography>
          </Box>
        ) : (
          <div {...getRootProps()}>
            <input {...getInputProps()} />
            <Box
              sx={{
                p: 2,
                border: "1px dashed grey",
                textAlign: "center",
              }}
            >
              {isDragActive ? (
                <Typography>Drop the files here ...</Typography>
              ) : (
                <Typography>
                  Drag 'n' drop some files here, or click to select files. Please upload <b>pdf</b> file only
                </Typography>
              )}
            </Box>
          </div>
        )}
      </Box>
      {fileUploads.length > 0 && (
        <Box sx={{ mt: 1.5, mb: 1 }}>
          {fileUploads.map((entry) => (
            <Box
              key={entry.id}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                px: 2,
                py: 1,
                mb: 0.5,
                borderRadius: 1,
                backgroundColor:
                  entry.status === "error"
                    ? "rgba(211,47,47,0.06)"
                    : entry.status === "success"
                    ? "rgba(46,125,50,0.06)"
                    : "rgba(25,118,210,0.06)",
                border: "1px solid",
                borderColor:
                  entry.status === "error"
                    ? "error.light"
                    : entry.status === "success"
                    ? "success.light"
                    : "info.light",
              }}
            >
              {entry.status === "uploading" && <CircularProgress size={20} />}
              {entry.status === "success" && <CheckCircleOutlineIcon color="success" fontSize="small" />}
              {entry.status === "error" && <ErrorOutlineIcon color="error" fontSize="small" />}

              <InsertDriveFileIcon sx={{ color: "text.secondary", fontSize: 20 }} />

              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                  {entry.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {formatFileSize(entry.size)}
                  {entry.status === "uploading" && " - Uploading..."}
                  {entry.status === "success" && " - Completed"}
                  {entry.status === "error" && ` - ${entry.error}`}
                </Typography>
              </Box>

              {entry.status !== "uploading" && (
                <IconButton size="small" onClick={() => removeFileUpload(entry.id)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
          ))}
        </Box>
      )}
      <Box
        m="40px 0 0 0"
        height="75vh"
        sx={{
          "& .MuiDataGrid-columnSeparator": {
            display: "none",
          },
          "& .MuiDataGrid-root": {
            border: "none",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "none",
          },
          "& .name-column--cell": {
            color: colors.blueAccent[600],
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: colors.grey[900],
            borderBottom: "none",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: colors.primary[400],
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: colors.grey[900],
          },
          "& .MuiCheckbox-root": {
            color: `${colors.grey[500]} !important`,
          },
        }}
      >
        {/* https://v4.mui.com/components/data-grid/pagination/#server-side-pagination */}
        <DataGrid 
          rows={aiPipeconeDocuments || []} 
          columns={columns} 
          loading={isLoading} 
          pagination 
          pageSize={pageSize} 
          rowsPerPageOptions={[pageSize]} 
          rowCount={totalCount} 
          paginationMode="server" 
          onPageChange={(newPage) => setPage(newPage)}
          checkboxSelection
          onSelectionModelChange={(newSelection) => {
            setSelectedRows(newSelection);
          }}
          selectionModel={selectedRows}
        />
      </Box>
      <ModalDialog
        title="Delete Selected"
        content={`Are you sure you want to delete ${selectedRows.length} selected document(s)? This will remove them from the vector store, S3, and database.`}
        isLoading={isDeletingSelected}
        open={openDeleteSelectedModal}
        handleOk={handleDeleteSelectedOk}
        handleCancel={() => setOpenDeleteSelectedModal(false)}
      />
      <Dialog open={openDeleteAllModal} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ px: 3, py: 2 }}>
          <Typography variant="h6" fontWeight={600}>Delete All Documents</Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ px: 3, py: 3 }}>
          {!isDeletingAll && !deleteAllProgress.done && (
            <Box>
              <Typography>
                Are you sure you want to delete <strong>all</strong> documents for{' '}
                <strong>{selectedcommodities[0]}</strong> / <strong>{selectedMarkets[0]}</strong>?
                This cannot be undone.
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                To confirm, type <strong>{selectedcommodities[0]} / {selectedMarkets[0]}</strong> below:
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder={`${selectedcommodities[0]} / ${selectedMarkets[0]}`}
                value={deleteAllConfirmText}
                onChange={(e) => setDeleteAllConfirmText(e.target.value)}
                sx={{ mt: 1 }}
              />
            </Box>
          )}
          {isDeletingAll && (
            <Box>
              <Box display="flex" justifyContent="space-between" alignItems="baseline" sx={{ mb: 0.5 }}>
                <Typography variant="body2" fontWeight={500}>
                  {deleteAllProgress.total > 0
                    ? `Deleting ${deleteAllProgress.deleted} of ${deleteAllProgress.total} files...`
                    : (deleteAllProgress.status || 'Starting...')}
                </Typography>
                {deleteAllProgress.total > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    {deleteAllProgress.deleted}/{deleteAllProgress.total}
                  </Typography>
                )}
              </Box>
              <LinearProgress
                variant={deleteAllProgress.total > 0 ? 'determinate' : 'indeterminate'}
                value={deleteAllProgress.total > 0 ? (deleteAllProgress.deleted / deleteAllProgress.total) * 100 : 0}
                sx={{ borderRadius: 1, height: 8 }}
              />
              {deleteAllProgress.current_file && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {deleteAllProgress.current_file}
                </Typography>
              )}
              <Box display="flex" justifyContent="space-between" sx={{ mt: 0.75 }}>
                <Typography variant="caption" color="text.secondary">
                  {deleteAllProgress.elapsed != null ? `Elapsed: ${Math.floor(deleteAllProgress.elapsed / 60)}:${String(Math.round(deleteAllProgress.elapsed % 60)).padStart(2, '0')}` : ''}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {deleteAllProgress.eta != null && deleteAllProgress.eta > 0
                    ? deleteAllProgress.eta < 60
                      ? 'under a minute remaining'
                      : `~${Math.ceil(deleteAllProgress.eta / 60)} min remaining`
                    : ''}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Clearing S3 and the database. OpenAI vector store cleanup continues in the background.
              </Typography>
            </Box>
          )}
          {deleteAllProgress.done && (
            <Box>
              <Typography>
                Successfully deleted <strong>{deleteAllProgress.deleted}</strong> documents from the database and S3.
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                OpenAI vector store cleanup runs in the background and may take a few minutes for large stores.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
          {!isDeletingAll && !deleteAllProgress.done && (
            <>
              <Button onClick={handleDeleteAllClose} color="inherit" variant="outlined">Cancel</Button>
              <Button
                onClick={handleDeleteAllOk}
                variant="contained"
                color="error"
                disabled={deleteAllConfirmText !== `${selectedcommodities[0]} / ${selectedMarkets[0]}`}
              >
                Delete All
              </Button>
            </>
          )}
          {deleteAllProgress.done && (
            <Button onClick={handleDeleteAllClose} variant="contained">Close</Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AIv2;
